import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Form } from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ROUTES, TABS } from '@common/constants';
import CommonHeader from '@components/CommonHeader';
import { AppContext } from '@context';

import history from '../../../historyData';
import OrganizationEmailConfigForm from '../components/OrganizationEmailConfigForm';
import OrganizationTab from '../components/OrganizationTab';
import { CREATE_EMAIL_CONFIG, UPDATE_EMAIL_CONFIG } from '../graphql/Mutations';
import { GET_EMAIL_CONFIG } from '../graphql/Queries';

const OrganizationEmailConfig = () => {
  const { orgId, orgMode } = useParams();
  const [form] = Form?.useForm();
  const { dispatch } = useContext(AppContext);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isView, setIsView] = useState(false);

  const [createOrganizationConfig, { loading: createLoading }] = useMutation(
    CREATE_EMAIL_CONFIG,
    {
      onError: () => {},
    },
  );

  const [
    updateOrganizationConfig,
    { loading: updateLoading },
  ] = useMutation(UPDATE_EMAIL_CONFIG, { onError: () => {} });

  const handleShowPrompt = () => {
    setIsDisabled(false);
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: true,
    });
  };

  const onCancelEdit = () => {
    setIsView(true);
    history?.push(`${ROUTES?.ORGANIZATIONS}`);
  };

  const [
    getOrganizationConfig,
    { data: organizationConfigData },
  ] = useLazyQuery(GET_EMAIL_CONFIG);

  const getOrganizationConfigDetail = async () => {
    try {
      await getOrganizationConfig({
        variables: { where: { companyId: orgId } },
        fetchPolicy: 'network-only',
      });
    } catch (error) {
      return error;
    }
  };

  const onFinish = async (values) => {
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: false,
    });

    if (organizationConfigData?.getCompanyConfig) {
      try {
        await updateOrganizationConfig({
          variables: {
            where: { id: organizationConfigData?.getCompanyConfig?.id },
            data: {
              emailConfig: {
                ...values,
                groupId: parseInt(values?.groupId, 10),
              },
            },
          },
          onCompleted: () => onCancelEdit(),
        });
      } catch (error) {
        return error;
      }
    } else {
      try {
        await createOrganizationConfig({
          variables: {
            data: {
              companyId: orgId,
              emailConfig: {
                ...values,
                groupId: parseInt(values?.groupId, 10),
              },
            },
          },
          onCompleted: () => onCancelEdit(),
        });
      } catch (error) {
        return error;
      }
    }
  };

  const onEdit = () => {
    setIsView(false);
    history?.push(
      `${ROUTES?.ORGANIZATIONS}/edit/${orgId}/${TABS?.ORGANIZATION_EMAIL_CONFIG?.key}`,
    );
  };

  useEffect(() => {
    if (orgId) {
      getOrganizationConfigDetail();
    }
    if (orgMode === 'view') {
      setIsView(true);
    }
  }, []);

  return (
    <>
      <CommonHeader
        heading={
          organizationConfigData?.getCompanyConfig
            ? `${orgMode} Email Config`
            : 'Add Email Config'
        }
        customLink={ROUTES?.ORGANIZATIONS}
      >
        <div className="header-btn-wrapper">
          {isView ? (
            <Button className="common-button" size="middle" onClick={onEdit}>
              Edit
            </Button>
          ) : (
            <>
              <Button
                type="primary"
                className="common-button mr-16"
                size="middle"
                onClick={onCancelEdit}
              >
                Cancel
              </Button>
              <Button
                className="common-button"
                size="middle"
                onClick={form?.submit}
                loading={createLoading || updateLoading}
                disabled={createLoading || updateLoading || isDisabled}
              >
                Save
              </Button>
            </>
          )}
        </div>
      </CommonHeader>
      <OrganizationTab>
        <OrganizationEmailConfigForm
          form={form}
          onFinish={onFinish}
          handleShowPrompt={handleShowPrompt}
          isView={isView}
          organizationConfigData={organizationConfigData?.getCompanyConfig}
        />
      </OrganizationTab>
    </>
  );
};

export default OrganizationEmailConfig;
